/**
 * NFCサービスインターフェース
 * プラットフォームに依存しない統一的なNFC操作を提供
 */

export interface NFCCardData {
  uid: string;
  type: number; // 1=スタッフ, 2=顧客
  entity_id: number;
  timestamp: string;
}

export interface NFCServiceEvent {
  event:
    | 'connected'
    | 'disconnected'
    | 'nfc_detected'
    | 'card_written'
    | 'error'
    | 'permission_denied';
  data?: NFCCardData | Record<string, unknown>;
  message?: string;
  context?: string;
}

export type NFCEventHandler = (event: NFCServiceEvent) => void;

/**
 * NFCサービスの抽象インターフェース
 * 各プラットフォーム固有の実装はこのインターフェースを実装する
 */
export interface INFCService {
  /**
   * サービスを初期化して接続
   */
  connect(): Promise<void>;

  /**
   * サービスを切断
   */
  disconnect(): void;

  /**
   * 接続状態を取得
   */
  isConnected(): boolean;

  /**
   * 手動でNFCスキャンを開始（Android用）
   */
  startManualScan?(): Promise<void>;

  /**
   * NFCカードへの書き込みをリクエスト
   */
  requestWriteCard(type: number, entityId: number): Promise<void>;

  /**
   * イベントハンドラを登録
   */
  on(event: string, handler: NFCEventHandler): () => void;

  /**
   * 特定のイベントハンドラを削除
   */
  off(event: string): void;

  /**
   * すべてのイベントハンドラを削除
   */
  clear(): void;

  /**
   * 書き込みモードかどうかを取得
   */
  isWriteMode(): boolean;

  /**
   * デバイスIDを取得
   */
  getDeviceId(): string;

  /**
   * サービスタイプを取得（デバッグ用）
   */
  getServiceType(): 'websocket' | 'web-nfc' | 'mock';
}

/**
 * NFCカードのバイナリデータ構造
 * NTAG215のページ4に格納される4バイトのデータ
 */
export interface NFCBinaryData {
  type: number; // 1バイト目: カード種別
  entityId: number; // 2-4バイト目: エンティティID（最大16,777,215）
}

/**
 * NFCカードデータのエンコード
 */
export function encodeNFCData(type: number, entityId: number): Uint8Array {
  if (entityId > 16777215) {
    throw new Error('Entity ID exceeds maximum value (16,777,215)');
  }

  return new Uint8Array([
    type & 0xff, // Type (1バイト)
    (entityId >> 16) & 0xff, // Entity ID 上位バイト
    (entityId >> 8) & 0xff, // Entity ID 中位バイト
    entityId & 0xff, // Entity ID 下位バイト
  ]);
}

/**
 * NFCカードデータのデコード
 */
export function decodeNFCData(buffer: Uint8Array): NFCBinaryData {
  if (buffer.length < 4) {
    throw new Error('Invalid buffer size for NFC data');
  }

  const type = buffer[0];
  const entityId = (buffer[1] << 16) | (buffer[2] << 8) | buffer[3];

  return { type, entityId };
}

/**
 * UIDのフォーマット検証
 */
export function validateUID(uid: string): boolean {
  // NTAG215のUIDは通常14文字の16進数（7バイト）
  return /^[0-9A-Fa-f]{14}$/i.test(uid);
}

/**
 * デバイスIDの生成または取得
 */
export function getOrCreateDeviceId(): string {
  const storageKey = 'device_id';
  let deviceId = localStorage.getItem(storageKey);

  if (!deviceId) {
    deviceId =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? `device_${crypto.randomUUID()}`
        : `device_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
    localStorage.setItem(storageKey, deviceId);
  }

  return deviceId;
}
